import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { api, ApiError } from '../lib/api';

type Tipo = 'musica' | 'playlist' | 'serie' | 'pelicula';
type Plataforma = 'spotify' | 'youtube' | 'apple_music' | 'amazon_prime' | 'netflix' | 'otro';

interface MusicaCineItem {
  id: string;
  titulo: string;
  tipo: Tipo;
  descripcion: string;
  foto_url: string;
  url_destino: string;
  plataforma: Plataforma;
  es_playlist: boolean;
  activo: boolean;
  orden: number;
  created_at?: string;
  updated_at?: string;
}

const TIPO_OPTIONS: { value: Tipo; label: string }[] = [
  { value: 'musica', label: 'Canción' },
  { value: 'playlist', label: 'Playlist / Álbum' },
  { value: 'serie', label: 'Serie' },
  { value: 'pelicula', label: 'Película' },
];

const PLATAFORMA_OPTIONS: { value: Plataforma; label: string; color: string }[] = [
  { value: 'spotify', label: 'Spotify', color: '#1DB954' },
  { value: 'youtube', label: 'YouTube', color: '#FF0000' },
  { value: 'apple_music', label: 'Apple Music', color: '#FA243C' },
  { value: 'amazon_prime', label: 'Amazon Prime', color: '#00A8E1' },
  { value: 'netflix', label: 'Netflix', color: '#E50914' },
  { value: 'otro', label: 'Otro', color: '#7B8D9E' },
];

type FormState = Omit<MusicaCineItem, 'id' | 'created_at' | 'updated_at'>;

const emptyForm = (): FormState => ({
  titulo: '',
  tipo: 'musica',
  descripcion: '',
  foto_url: '',
  url_destino: '',
  plataforma: 'spotify',
  es_playlist: false,
  activo: true,
  orden: 0,
});

export default function MusicaCinePage() {
  const [items, setItems] = useState<MusicaCineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // activo vacío para que devuelva activos+inactivos
      const data = await api.get<MusicaCineItem[]>('/musica-cine?activo=&limit=200');
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudieron cargar los items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(true);
    setFeedback(null);
  };

  const openEdit = (item: MusicaCineItem) => {
    setEditingId(item.id);
    setForm({
      titulo: item.titulo,
      tipo: item.tipo,
      descripcion: item.descripcion,
      foto_url: item.foto_url,
      url_destino: item.url_destino,
      plataforma: item.plataforma,
      es_playlist: item.es_playlist,
      activo: item.activo,
      orden: item.orden ?? 0,
    });
    setFormOpen(true);
    setFeedback(null);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      if (editingId) {
        await api.put(`/musica-cine/${editingId}`, form);
        setFeedback({ kind: 'success', text: 'Actualizado correctamente' });
      } else {
        await api.post('/musica-cine', form);
        setFeedback({ kind: 'success', text: 'Creado correctamente' });
      }
      setFormOpen(false);
      setEditingId(null);
      setForm(emptyForm());
      await load();
    } catch (err) {
      setFeedback({ kind: 'error', text: err instanceof ApiError ? err.message : 'Error al guardar' });
    } finally {
      setSaving(false);
    }
  };

  const onToggle = async (item: MusicaCineItem) => {
    try {
      const r = await api.patch<{ activo: boolean }>(`/musica-cine/${item.id}/toggle`);
      setItems(prev => prev.map(x => x.id === item.id ? { ...x, activo: r.activo } : x));
    } catch (err) {
      setFeedback({ kind: 'error', text: err instanceof ApiError ? err.message : 'No se pudo cambiar el estado' });
    }
  };

  const onDelete = async (item: MusicaCineItem) => {
    if (!window.confirm(`¿Eliminar "${item.titulo}"?`)) return;
    try {
      await api.del(`/musica-cine/${item.id}`);
      setItems(prev => prev.filter(x => x.id !== item.id));
    } catch (err) {
      setFeedback({ kind: 'error', text: err instanceof ApiError ? err.message : 'No se pudo eliminar' });
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      i.titulo.toLowerCase().includes(q) ||
      i.descripcion.toLowerCase().includes(q) ||
      i.plataforma.toLowerCase().includes(q)
    );
  }, [items, search]);

  const plataformaColor = (p: Plataforma) =>
    PLATAFORMA_OPTIONS.find(o => o.value === p)?.color || '#7B8D9E';
  const plataformaLabel = (p: Plataforma) =>
    PLATAFORMA_OPTIONS.find(o => o.value === p)?.label || p;
  const tipoLabel = (t: Tipo) =>
    TIPO_OPTIONS.find(o => o.value === t)?.label || t;

  return (
    <>
      <h1 className="page-title">Música y Cine ({items.length})</h1>

      {feedback && (
        <div className={'alert ' + (feedback.kind === 'error' ? 'alert-error' : 'alert-success')}>
          {feedback.text}
        </div>
      )}

      <div className="card" style={{ padding: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          style={{ flex: 1, minWidth: 220, padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
          placeholder="Buscar por título, descripción o plataforma…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="btn" onClick={openCreate}>+ Nueva recomendación</button>
      </div>

      {formOpen && (
        <div className="card">
          <h2 className="section-title">{editingId ? 'Editar recomendación' : 'Nueva recomendación'}</h2>
          <form onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="mc-titulo">Título *</label>
              <input
                id="mc-titulo"
                type="text"
                required
                value={form.titulo}
                onChange={e => onChange('titulo', e.target.value)}
                placeholder="Ej. Inmaculada — Mavi Sánchez"
              />
            </div>

            <div className="field">
              <label htmlFor="mc-tipo">Tipo *</label>
              <select id="mc-tipo" value={form.tipo} onChange={e => onChange('tipo', e.target.value as Tipo)}>
                {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className="field">
              <label htmlFor="mc-plataforma">Plataforma *</label>
              <select id="mc-plataforma" value={form.plataforma} onChange={e => onChange('plataforma', e.target.value as Plataforma)}>
                {PLATAFORMA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className="field">
              <label htmlFor="mc-url">URL de destino *</label>
              <input
                id="mc-url"
                type="url"
                required
                value={form.url_destino}
                onChange={e => onChange('url_destino', e.target.value)}
                placeholder="https://open.spotify.com/..."
              />
            </div>

            <div className="field">
              <label htmlFor="mc-descripcion">Descripción *</label>
              <textarea
                id="mc-descripcion"
                required
                value={form.descripcion}
                onChange={e => onChange('descripcion', e.target.value)}
                placeholder="Por qué recomendamos este contenido…"
              />
            </div>

            <div className="field">
              <label htmlFor="mc-foto">URL de foto</label>
              <input
                id="mc-foto"
                type="url"
                value={form.foto_url}
                onChange={e => onChange('foto_url', e.target.value)}
                placeholder="https://…/portada.jpg"
              />
              {form.foto_url && (
                <div style={{ marginTop: 8, width: 140, height: 140, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
                  <img
                    src={form.foto_url}
                    alt="preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div className="field" style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
              <input
                id="mc-playlist"
                type="checkbox"
                checked={form.es_playlist}
                onChange={e => onChange('es_playlist', e.target.checked)}
              />
              <label htmlFor="mc-playlist" style={{ margin: 0 }}>¿Es playlist / colección?</label>
            </div>

            <div className="field">
              <label htmlFor="mc-orden">Orden</label>
              <input
                id="mc-orden"
                type="number"
                value={form.orden}
                onChange={e => onChange('orden', Number(e.target.value) || 0)}
              />
              <small className="muted">Menor número aparece primero.</small>
            </div>

            <div className="field" style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
              <input
                id="mc-activo"
                type="checkbox"
                checked={form.activo}
                onChange={e => onChange('activo', e.target.checked)}
              />
              <label htmlFor="mc-activo" style={{ margin: 0 }}>Activo (visible en la app)</label>
            </div>

            <div className="btn-row">
              <button type="submit" className="btn" disabled={saving}>
                {saving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Crear recomendación'}
              </button>
              <button
                type="button"
                className="btn-ghost btn"
                onClick={() => { setFormOpen(false); setEditingId(null); setForm(emptyForm()); }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <h2 className="section-title">Listado ({filtered.length})</h2>
        {loading ? <p className="muted">Cargando…</p> :
         error ? <div className="alert alert-error">{error}</div> :
         filtered.length === 0 ? (
           <div className="empty">{items.length === 0 ? 'Sin recomendaciones aún' : 'Sin resultados para esa búsqueda'}</div>
         ) : filtered.map(item => (
           <div key={item.id} className="missionary-row">
             <div
               className="missionary-avatar"
               style={{ background: 'var(--bg)', borderRadius: 10 }}
             >
               {item.foto_url
                 ? <img src={item.foto_url} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                 : (item.titulo || '?').charAt(0).toUpperCase()}
             </div>
             <div className="missionary-meta">
               <div className="name">{item.titulo}</div>
               <div className="email" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                 <span
                   style={{
                     background: plataformaColor(item.plataforma),
                     color: '#fff',
                     fontSize: 11,
                     fontWeight: 600,
                     padding: '2px 8px',
                     borderRadius: 10,
                   }}
                 >
                   {plataformaLabel(item.plataforma)}
                 </span>
                 <span
                   style={{
                     border: '1px solid var(--border)',
                     fontSize: 11,
                     padding: '2px 8px',
                     borderRadius: 10,
                     color: 'var(--text-muted)',
                   }}
                 >
                   {tipoLabel(item.tipo)}
                 </span>
                 {item.orden ? <span className="muted" style={{ fontSize: 11 }}>orden: {item.orden}</span> : null}
               </div>
             </div>
             <button
               className={'status-pill' + (item.activo ? '' : ' off')}
               onClick={() => onToggle(item)}
               title="Cambiar visibilidad"
             >
               {item.activo ? 'Activo' : 'Oculto'}
             </button>
             <button className="icon-btn" title="Editar" onClick={() => openEdit(item)}>✎</button>
             <button className="icon-btn danger" title="Eliminar" onClick={() => onDelete(item)}>🗑</button>
           </div>
         ))
        }
      </div>
    </>
  );
}
